import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Wifi, Battery, Signal, RotateCcw } from 'lucide-react';

interface FlowNode {
    id: number;
    nome: string;
    type_name: string;
    message?: string | null;
    title?: string | null;
    subtitle?: string | null;
    media_url?: string | null;
    media_type?: string | null;
    media_filename?: string | null;
    delay_seconds?: number | null;
    tag_id?: number | null;
    tag_nome?: string;
    tag_cor?: string;
    triggers?: Array<{ trigger_type: string; trigger_value: string }>;
    buttons?: Array<{ id: number; button_text: string; button_type: string; button_value?: string; url?: string }>;
}

interface Transition {
    from_node_id: number;
    to_node_id: number;
    to_button_id?: number | null;
}

interface RenderedItem {
    id: string;
    kind: 'bot' | 'user' | 'delay' | 'tag';
    node?: FlowNode;
    text?: string;
}

interface FlowPreviewModalProps {
    open: boolean;
    onClose: () => void;
    nodes: FlowNode[];
    transitions: Transition[];
    pageName?: string;
}

export default function FlowPreviewModal({ open, onClose, nodes, transitions, pageName }: FlowPreviewModalProps) {
    const [items, setItems] = useState<RenderedItem[]>([]);
    const [isTyping, setIsTyping] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const abortRef = useRef(false);
    const runIdRef = useRef(0);

    const displayName = pageName || 'Sua Página';
    const initials = displayName.substring(0, 2).toUpperCase();

    const now = new Date();
    const timeStr = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    // Auto-scroll
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [items, isTyping]);

    // Helpers
    const getDefaultNext = useCallback((fromId: number): FlowNode | null => {
        const t = transitions.find(tr => tr.from_node_id === fromId && !tr.to_button_id);
        return t ? (nodes.find(n => n.id === t.to_node_id) || null) : null;
    }, [transitions, nodes]);

    const getButtonNext = useCallback((fromId: number, btnId: number): FlowNode | null => {
        const t = transitions.find(tr => tr.from_node_id === fromId && tr.to_button_id === btnId);
        return t ? (nodes.find(n => n.id === t.to_node_id) || null) : null;
    }, [transitions, nodes]);

    const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

    const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

    // Deliver a chain of nodes (non-blocking, follows default transitions)
    const deliverChain = useCallback(async (startNode: FlowNode, runId: number) => {
        let current: FlowNode | null = startNode;

        while (current && !abortRef.current && runIdRef.current === runId) {
            const node = current;

            // Tag
            if (node.type_name === 'tag') {
                setItems(prev => [...prev, { id: uid(), kind: 'tag', node }]);
                current = getDefaultNext(node.id);
                await sleep(300);
                continue;
            }

            // Dedicated delay node
            if (node.type_name === 'delay') {
                setItems(prev => [...prev, { id: uid(), kind: 'delay', node }]);
                await sleep(Math.min((node.delay_seconds || 1) * 1000, 3000));
                if (abortRef.current || runIdRef.current !== runId) break;
                current = getDefaultNext(node.id);
                continue;
            }

            // Typing indicator
            setIsTyping(true);
            await sleep(node.type_name === 'audio' ? 600 : 1000);
            if (abortRef.current || runIdRef.current !== runId) { setIsTyping(false); break; }
            setIsTyping(false);

            // Add message
            setItems(prev => [...prev, { id: uid(), kind: 'bot', node }]);

            await sleep(400);
            if (abortRef.current || runIdRef.current !== runId) break;

            // If has non-url buttons, STOP — user must click to branch
            const actionButtons = node.buttons?.filter(b => b.button_type !== 'url');
            if (actionButtons && actionButtons.length > 0) {
                break;
            }

            // Inline delay_seconds — pause after this message, before the next
            if (node.delay_seconds && node.delay_seconds > 0) {
                setItems(prev => [...prev, { id: uid(), kind: 'delay', node: { ...node, delay_seconds: node.delay_seconds } }]);
                await sleep(Math.min(node.delay_seconds * 1000, 3000));
                if (abortRef.current || runIdRef.current !== runId) break;
            }

            current = getDefaultNext(node.id);
        }
    }, [getDefaultNext]);

    // Start flow
    const startFlow = useCallback(() => {
        const trigger = nodes.find(n => n.type_name === 'trigger');
        if (!trigger) return;
        const first = getDefaultNext(trigger.id);
        if (!first) return;

        const runId = ++runIdRef.current;
        abortRef.current = false;
        setItems([]);
        setIsTyping(false);

        setTimeout(() => deliverChain(first, runId), 500);
    }, [nodes, getDefaultNext, deliverChain]);

    // Init on open
    useEffect(() => {
        if (open) startFlow();
        return () => { abortRef.current = true; };
    }, [open, startFlow]);

    // Button click handler
    const handleButtonClick = useCallback((nodeId: number, btnId: number, btnText: string, btnType: string, btnValue?: string) => {
        // URL button → open in new tab, don't advance flow
        if (btnType === 'url' && btnValue) {
            window.open(btnValue, '_blank', 'noopener,noreferrer');
            return;
        }

        // Add user reply bubble
        setItems(prev => [...prev, { id: uid(), kind: 'user', text: btnText }]);

        // Find next node via this button's transition
        const next = getButtonNext(nodeId, btnId);
        if (!next) return;

        const runId = ++runIdRef.current;
        abortRef.current = false;

        setTimeout(() => deliverChain(next, runId), 500);
    }, [getButtonNext, deliverChain]);

    // Restart
    const handleRestart = useCallback(() => {
        abortRef.current = true;
        setTimeout(() => startFlow(), 100);
    }, [startFlow]);

    const handleClose = useCallback(() => {
        abortRef.current = true;
        onClose();
    }, [onClose]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

            <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
                <div className="relative pointer-events-auto">
                    {/* Top Controls */}
                    <div className="absolute -top-12 left-0 right-0 flex items-center justify-between">
                        <button
                            onClick={handleRestart}
                            className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white rounded-full px-3 py-1.5 text-xs font-medium transition-colors backdrop-blur-sm"
                        >
                            <RotateCcw className="w-3.5 h-3.5" />
                            Reiniciar
                        </button>
                        <button
                            onClick={handleClose}
                            className="bg-white/10 hover:bg-white/20 text-white rounded-full p-1.5 transition-colors backdrop-blur-sm"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Phone Frame */}
                    <div className="w-[375px] h-[720px] bg-[#0b1120] rounded-[3rem] p-3 shadow-2xl border border-gray-700/50">
                        <div className="w-full h-full bg-[#111827] rounded-[2.3rem] overflow-hidden flex flex-col">
                            {/* Status Bar */}
                            <div className="flex items-center justify-between px-8 pt-3 pb-1">
                                <span className="text-white text-xs font-medium">{timeStr}</span>
                                <div className="w-28 h-6 bg-black rounded-full mx-auto" />
                                <div className="flex items-center gap-1">
                                    <Signal className="w-3.5 h-3.5 text-white" strokeWidth={2} />
                                    <Wifi className="w-3.5 h-3.5 text-white" strokeWidth={2} />
                                    <Battery className="w-4 h-4 text-white" strokeWidth={2} />
                                </div>
                            </div>

                            {/* Header */}
                            <div className="flex items-center gap-3 px-4 py-2.5 border-b border-white/5">
                                <div className="relative shrink-0">
                                    <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                                        <span className="text-white font-bold text-xs">{initials}</span>
                                    </div>
                                    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-[#111827]" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-white text-sm font-semibold truncate">{displayName}</p>
                                    <p className="text-green-400 text-[10px]">Business chat</p>
                                </div>
                            </div>

                            {/* Messages */}
                            <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2" style={{ scrollBehavior: 'smooth' }}>
                                {items.map((item) => {
                                    // User reply bubble (right side)
                                    if (item.kind === 'user') {
                                        return (
                                            <div key={item.id} className="flex justify-end animate-in slide-in-from-bottom-2 duration-300">
                                                <div className="bg-blue-600 rounded-2xl rounded-br-md px-3.5 py-2.5 max-w-[220px]">
                                                    <p className="text-[13px] text-white">{item.text}</p>
                                                </div>
                                            </div>
                                        );
                                    }

                                    // Tag system message
                                    if (item.kind === 'tag' && item.node) {
                                        return (
                                            <div key={item.id} className="flex items-center justify-center py-1">
                                                <div className="bg-white/5 border border-white/10 rounded-full px-3 py-1">
                                                    <span className="text-[10px] text-gray-400">
                                                        🏷️ Tag <span className="font-medium" style={{ color: item.node.tag_cor || '#fff' }}>{item.node.tag_nome || 'aplicada'}</span>
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    }

                                    // Delay separator
                                    if (item.kind === 'delay' && item.node) {
                                        return (
                                            <div key={item.id} className="flex items-center justify-center gap-2 py-1">
                                                <div className="h-px flex-1 bg-white/10" />
                                                <span className="text-[10px] text-gray-500 uppercase tracking-wider">
                                                    ⏱ {item.node.delay_seconds}s
                                                </span>
                                                <div className="h-px flex-1 bg-white/10" />
                                            </div>
                                        );
                                    }

                                    // Bot message
                                    if (item.kind === 'bot' && item.node) {
                                        const node = item.node;
                                        const hasButtons = node.buttons && node.buttons.length > 0;
                                        const hasText = !!(node.title || node.message);
                                        const hasMedia = !!node.media_url;
                                        const isTemplate = node.type_name.includes('template');

                                        return (
                                            <div key={item.id} className="flex items-end gap-1.5 animate-in slide-in-from-bottom-2 duration-300">
                                                {/* Avatar */}
                                                <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shrink-0 mb-0.5">
                                                    <span className="text-white text-[8px] font-bold">{initials[0]}</span>
                                                </div>

                                                <div className="max-w-[260px]">
                                                    {/* Card container: text + buttons unified */}
                                                    {(node.type_name === 'message' || node.type_name === 'message_template' ||
                                                        node.type_name === 'photo_template' || node.type_name === 'video_template') && (
                                                            <div className="bg-[#1e293b] rounded-2xl rounded-bl-md overflow-hidden">
                                                                {/* Media inside card */}
                                                                {/* aspect-[1.91/1]: e a proporcao que a Meta aplica no Generic
                                                                    Template quando image_aspect_ratio nao e definido (default
                                                                    "horizontal"). Antes era h-40 num balao de 260px = 1,63:1, entao
                                                                    o preview mostrava MAIS imagem do que o Messenger — o CTA
                                                                    aparecia aqui e sumia no envio real. */}
                                                                {hasMedia && node.type_name === 'photo_template' && (
                                                                    <img src={node.media_url!} alt="" className="w-full aspect-[1.91/1] object-cover" />
                                                                )}
                                                                {hasMedia && node.type_name === 'video_template' && (
                                                                    <div className="relative">
                                                                        <video src={node.media_url!} className="w-full aspect-[1.91/1] object-cover" preload="metadata" />
                                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                                            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                                                                                <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                                                                    <path d="M8 5v14l11-7z" />
                                                                                </svg>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Text */}
                                                                {hasText && (
                                                                    <div className="px-3.5 py-2.5">
                                                                        <p className="text-[13px] text-gray-200 whitespace-pre-wrap break-words leading-relaxed">
                                                                            {node.title || node.message}
                                                                        </p>
                                                                        {isTemplate && node.subtitle && (
                                                                            <p className="text-[11px] text-gray-400 mt-1 whitespace-pre-wrap">{node.subtitle}</p>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {/* Buttons inside card */}
                                                                {hasButtons && (
                                                                    <div className="border-t border-white/10">
                                                                        {node.buttons!.map((btn, bi) => (
                                                                            <button
                                                                                key={btn.id}
                                                                                onClick={() => handleButtonClick(node.id, btn.id, btn.button_text, btn.button_type, btn.button_value)}
                                                                                className={`w-full py-2.5 px-3 text-center hover:bg-white/5 transition-colors active:scale-[0.98] ${bi < node.buttons!.length - 1 ? 'border-b border-white/10' : ''}`}
                                                                            >
                                                                                <span className="text-[13px] font-medium flex items-center justify-center gap-1.5">
                                                                                    {btn.button_type === 'url' && (
                                                                                        <svg className="w-3 h-3 text-blue-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                                        </svg>
                                                                                    )}
                                                                                    <span className="text-blue-400">{btn.button_text}</span>
                                                                                </span>
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                    {/* Simple photo */}
                                                    {node.type_name === 'photo' && node.media_url && (
                                                        <div className="rounded-2xl rounded-bl-md overflow-hidden">
                                                            <img src={node.media_url} alt="" className="w-full aspect-[1.91/1] object-cover" />
                                                        </div>
                                                    )}

                                                    {/* Simple video */}
                                                    {node.type_name === 'video' && node.media_url && (
                                                        <div className="rounded-2xl rounded-bl-md overflow-hidden bg-black relative">
                                                            <video src={node.media_url} className="w-full h-44 object-cover" preload="metadata" />
                                                            <div className="absolute inset-0 flex items-center justify-center">
                                                                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                                                                    <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                                                        <path d="M8 5v14l11-7z" />
                                                                    </svg>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Audio */}
                                                    {node.type_name === 'audio' && (
                                                        <div className="bg-[#1e293b] rounded-2xl rounded-bl-md p-3 flex items-center gap-2.5">
                                                            <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center shrink-0">
                                                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                                                    <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
                                                                </svg>
                                                            </div>
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-1">
                                                                    {[...Array(18)].map((_, j) => (
                                                                        <div key={j} className="w-1 bg-blue-400/60 rounded-full" style={{ height: `${Math.random() * 14 + 4}px` }} />
                                                                    ))}
                                                                </div>
                                                                <p className="text-[10px] text-gray-500 mt-1">🎵 Áudio</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }

                                    return null;
                                })}

                                {/* Typing Indicator */}
                                {isTyping && (
                                    <div className="flex items-end gap-1.5 animate-in fade-in duration-200">
                                        <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shrink-0 mb-0.5">
                                            <span className="text-white text-[8px] font-bold">{initials[0]}</span>
                                        </div>
                                        <div className="bg-[#1e293b] rounded-2xl rounded-bl-md px-4 py-3">
                                            <div className="flex items-center gap-1">
                                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Input Bar */}
                            <div className="px-3 py-2.5 border-t border-white/5 bg-[#111827]">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-blue-600/20 flex items-center justify-center shrink-0">
                                        <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="12" y1="8" x2="12" y2="16" />
                                            <line x1="8" y1="12" x2="16" y2="12" />
                                        </svg>
                                    </div>
                                    <div className="flex-1 bg-white/5 border border-white/10 rounded-full px-3.5 py-2">
                                        <p className="text-[13px] text-gray-500">Aa</p>
                                    </div>
                                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center shrink-0">
                                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            {/* Home Indicator */}
                            <div className="flex justify-center pb-2 pt-1">
                                <div className="w-32 h-1 bg-white/20 rounded-full" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
